import { ReactElement, SyntheticEvent, useCallback, useMemo } from 'react';
import { Stack, Tooltip } from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { compact, omit, isEqual } from 'lodash';

import { useSubscriptionFeatures, useToggle } from '~hooks/index';
import { AccessFeature } from '~components/index';
import { PlanFeaturesTypes } from '~globals/types/enums';
import { Nullable } from '~globals/types/commons';
import { ItemPresetParse } from '~services/item/types';
import { convertItemSearchFiltersToItemOrCollectFilter } from '~utils/order';

import { OrdersFiltersPresetsV2Props } from './types';
import {
  OrdersFiltersPresetsV2DialogCreate,
  OrdersFiltersPresetsV2DialogConfirmEdit,
  OrdersFiltersPresetsV2DialogConfirmDelete,
} from './Dialogs';

const OrdersFiltersPresetsV2 = ({
  options,
  loading,
  filterTabId,
  currentFilters,
  selectedPreset,
  onChangePreset,
  actionsDialogs,
}: OrdersFiltersPresetsV2Props): ReactElement => {
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const handleChangePreset = useCallback(
    (_e: SyntheticEvent<Element, Event>, newVal: Nullable<ItemPresetParse>) => {
      onChangePreset(newVal);
    },
    [onChangePreset],
  );

  const isEmptyFilters = useMemo(() => {
    const filters = [
      // COMMONS
      currentFilters.search,
      currentFilters.expirationDateTimeFrom,
      currentFilters.expirationDateTimeTo,
      currentFilters.includeWithoutExpirationDate?.toString(),
      currentFilters.dateFrom,
      currentFilters.dateTo,
      // SPECIFIC ITEMS
      currentFilters.finalizedDateTimeFrom,
      currentFilters.finalizedDateTimeTo,
      currentFilters.unit1Gt,
      currentFilters.unit1Lt,
      currentFilters.unit2Gt,
      currentFilters.unit2Lt,
      currentFilters.priceGt,
      currentFilters.priceLt,
      currentFilters.vehicles?.length,
      currentFilters.drivers?.length,
      currentFilters.services?.length,
      currentFilters.zones?.length,
      currentFilters.assignedUser,
      currentFilters.sourceImports?.length,
      currentFilters.itemState?.toString(),
      // SPECIFIC COLLECT
      currentFilters.giverCompany,
      currentFilters.driver,
      currentFilters.collectState?.toString(),
    ];

    const compactCurrentFilters = compact(filters);

    return compactCurrentFilters.length === 0;
  }, [currentFilters]);

  const hasChangeInCurrentFilters = useMemo(() => {
    if (selectedPreset?.data) {
      const currentFiltersParsed =
        convertItemSearchFiltersToItemOrCollectFilter(
          filterTabId,
          currentFilters,
          {
            limitNoPro: hasAccessToFeature(PlanFeaturesTypes.LimitNotPro),
            mgmZones: hasAccessToFeature(PlanFeaturesTypes.MgmZones),
          },
        );

      const selectPresetFilter = omit(selectedPreset.data, [
        'itemImportStateTypeId',
        'exportCsv',
        'pagination',
        'mapMode',
      ]);

      return isEqual(currentFiltersParsed, selectPresetFilter);
    }

    return true;
  }, [filterTabId, currentFilters, selectedPreset, hasAccessToFeature]);

  const isNotValidToSave = useMemo(() => {
    if (!hasAccessToFeature(PlanFeaturesTypes.LimitPaid)) return true;

    if (selectedPreset) return hasChangeInCurrentFilters;

    return isEmptyFilters;
  }, [
    hasAccessToFeature,
    selectedPreset,
    hasChangeInCurrentFilters,
    isEmptyFilters,
  ]);

  const [openDialogCreate, toogleOpenDialogCreate] = useToggle(false);

  const [openDialogConfirmEdit, toogleOpenDialogConfirmEdit] = useToggle(false);

  const tooltipSaveMessage = useMemo(
    () => `${selectedPreset ? 'Editar' : 'Guardar'} filtro`,
    [selectedPreset],
  );

  const handleOpenDialogSave = useCallback(() => {
    if (selectedPreset) {
      toogleOpenDialogConfirmEdit(true);
      return;
    }

    toogleOpenDialogCreate(true);
  }, [selectedPreset, toogleOpenDialogConfirmEdit, toogleOpenDialogCreate]);

  const handleCreatePreset = useCallback(
    (currentPreset: ItemPresetParse) => {
      toogleOpenDialogCreate(false);
      actionsDialogs.create.onSuccess(currentPreset);
    },
    [actionsDialogs.create, toogleOpenDialogCreate],
  );

  const handleEditPreset = useCallback(
    (currentPreset: ItemPresetParse) => {
      toogleOpenDialogConfirmEdit(false);
      actionsDialogs.confirmEdit.onSuccess(currentPreset);
    },
    [actionsDialogs.confirmEdit, toogleOpenDialogConfirmEdit],
  );

  const [openDialogConfirmDelete, toogleOpenDialogConfirmDelete] =
    useToggle(false);

  const handleDeletePreset = useCallback(
    (currentPreset: ItemPresetParse) => {
      toogleOpenDialogConfirmDelete(false);
      actionsDialogs.confirmDelete.onSuccess(currentPreset);
    },
    [actionsDialogs.confirmDelete, toogleOpenDialogConfirmDelete],
  );

  return (
    <Stack direction="row" spacing={1} flex={1}>
      <AccessFeature.AutoComplete
        validFeature={PlanFeaturesTypes.LimitPaid}
        options={options}
        loading={loading}
        label="Filtros personalizados"
        name="filterPreset"
        value={selectedPreset}
        onChange={handleChangePreset}
        fullWidth
        getOptionLabel={(option) => option.description}
      />

      <Stack direction="row" spacing={1}>
        <Tooltip title={tooltipSaveMessage} arrow>
          <span style={{ display: 'flex' }}>
            <AccessFeature.Button
              validFeature={PlanFeaturesTypes.LimitPaid}
              showChip={false}
              variant="outlined"
              color="secondary"
              onClick={handleOpenDialogSave}
              disabled={isNotValidToSave}
              sx={(currenTheme) => ({
                minWidth: 0,
                padding: currenTheme.spacing(0, 1),
              })}
            >
              {selectedPreset ? <EditIcon /> : <SaveIcon />}
            </AccessFeature.Button>
          </span>
        </Tooltip>

        <Tooltip title="Eliminar filtro" arrow>
          <span style={{ display: 'flex' }}>
            <AccessFeature.Button
              validFeature={PlanFeaturesTypes.LimitPaid}
              showChip={false}
              variant="outlined"
              color="error"
              onClick={() => toogleOpenDialogConfirmDelete(true)}
              disabled={!selectedPreset}
              sx={(currenTheme) => ({
                minWidth: 0,
                padding: currenTheme.spacing(0, 1),
              })}
            >
              <DeleteIcon />
            </AccessFeature.Button>
          </span>
        </Tooltip>
      </Stack>

      <OrdersFiltersPresetsV2DialogCreate
        open={openDialogCreate}
        onClose={() => toogleOpenDialogCreate(false)}
        filterTabId={filterTabId}
        currentFilters={currentFilters}
        onSuccess={handleCreatePreset}
      />

      {selectedPreset && (
        <OrdersFiltersPresetsV2DialogConfirmEdit
          open={openDialogConfirmEdit}
          onClose={() => toogleOpenDialogConfirmEdit(false)}
          selectedPreset={selectedPreset}
          filterTabId={filterTabId}
          currentFilters={currentFilters}
          onSuccess={handleEditPreset}
        />
      )}

      {selectedPreset && (
        <OrdersFiltersPresetsV2DialogConfirmDelete
          open={openDialogConfirmDelete}
          onClose={() => toogleOpenDialogConfirmDelete(false)}
          selectedPreset={selectedPreset}
          onSuccess={handleDeletePreset}
        />
      )}
    </Stack>
  );
};

export default OrdersFiltersPresetsV2;
